import { AppFeature } from './app-feature';

export class AppPlan {
  /** Stable identifier used across the app (new plans use human-readable ids). */
  id?: string;
  uid!: string;
  name!: string;
  order!: number;
  isFeatured!: boolean;
  hasPrice!: boolean;

  /** @deprecated Legacy dollars-as-string fields (kept for compatibility). */
  price?: string;
  /** @deprecated Legacy display field (kept for compatibility). */
  discountPrice?: string;
  description?: string;
  subtitle?: string;
  createdAt!: string;
  updatedAt!: string;

  /** Feature list used by plan cards (supports legacy AppFeature objects or simple strings). */
  features!:
    | AppFeature[]
    | Record<string, AppFeature>
    | string[]
    | Record<string, any>;

  // New pricing schema (cents-based)
  tier?: number;
  billing?: Array<'monthly' | 'annual' | 'free'>;
  priceMonthlyCents?: number;
  priceAnnualCents?: number;
  includedAssets?: number;
  includedUsers?: number;
  retentionDays?: number;

  // Freemium/tier system fields (legacy, still used by existing flows)
  appTier?: 'free' | 'trial' | 'premium';
  isFree?: boolean;
  trialDays?: number;
  aiQueriesPerDay?: number;
  maxUsers?: number;
  icon?: string;

  flags?: {
    public?: boolean;
    selfServe?: boolean;
    contactSales?: boolean;
  };

  // Bring Your Own Scanner (BYOS) configuration
  byos?: {
    enabled: boolean;
    notes?: string;
  };

  // Optional add-ons available for this plan
  /**
   * @deprecated Add-ons are defined in `app-addons.json` now.
   * This field may exist on older plan objects.
   */
  addOns?: any[];

  // ---------------------------------------------------------------------------
  // Ninja Coins — plan credit allotments (new schema)
  // ---------------------------------------------------------------------------

  /**
   * Monthly Ninja Coins included with this plan.
   * 0 = no credits (free/expired tiers).
   */
  monthlyCredits?: number;

  /**
   * For annual plans: monthly credits granted per cycle (base × 1.10).
   * Computed as `Math.floor((monthlyCredits ?? 0) * 1.10)`.
   */
  annualMonthlyCredits?: number;

  /**
   * Maximum rollover credits (50% of monthlyCredits, caps at plan rollover cap).
   */
  creditRolloverCap?: number;

  constructor(data: object) {
    Object.assign(this, data);

    // Normalize id/uid for older data.
    if (!this.uid && (this as any).id) {
      this.uid = (this as any).id;
    }
    if (!this.id && this.uid) {
      this.id = this.uid;
    }

    // Derive app-tier from numeric tier when provided.
    // 0 = free. 1-3 = trial-eligible self-serve. 4 = premium/contact sales.
    if (typeof (this as any).tier === 'number' && !this.appTier) {
      const numericTier = (this as any).tier as number;
      this.appTier = numericTier === 0 ? 'free' : numericTier >= 4 ? 'premium' : 'trial';
    }

    // Backfill maxUsers from includedUsers (new schema) for legacy code paths.
    if (
      (this.maxUsers === undefined || this.maxUsers === null) &&
      typeof this.includedUsers === 'number'
    ) {
      this.maxUsers = this.includedUsers;
    }

    // Normalize boolean fields to avoid undefined -> false flips during dev-mode change detection.
    this.isFeatured = !!this.isFeatured;
    this.hasPrice = !!this.hasPrice;
    this.isFree = !!this.isFree;

    // Derive annualMonthlyCredits from monthlyCredits if not explicitly set.
    if (this.monthlyCredits !== undefined && this.annualMonthlyCredits === undefined) {
      this.annualMonthlyCredits = Math.floor(this.monthlyCredits * 1.10);
    }

    // Derive creditRolloverCap (50% of monthly) if not set.
    if (this.monthlyCredits !== undefined && this.creditRolloverCap === undefined) {
      this.creditRolloverCap = Math.floor(this.monthlyCredits * 0.50);
    }
  }
}
